import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const AddressAutofill = ({ address, onChange, idPrefix = '' }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localAddress, setLocalAddress] = useState(address || {});
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Generate unique ID prefix for this instance
  const [instanceId] = useState(() => idPrefix || `addr-${Math.random().toString(36).substr(2, 9)}`);

  // Sync local address state with prop changes
  useEffect(() => {
    setLocalAddress(address || {});
  }, [address]);

  // Search for addresses using the backend API proxy
  const searchAddresses = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${getApiBase()}/places/autocomplete`, {
        params: {
          query: query,
          types: 'address'
        },
        headers: getAuthHeaders()
      });

      if (response.data.status === 'OK' && response.data.predictions) {
        setSuggestions(response.data.predictions.slice(0, 6));
      } else if (response.data.status === 'ZERO_RESULTS') {
        setSuggestions([]);
      } else {
        console.warn('Google Places API returned status:', response.data.status);
        setError('Unable to fetch address suggestions');
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Error fetching address suggestions:', err);
      if (err.response?.status === 503) {
        setError('Address search is not configured. Contact your administrator.');
      } else {
        setError('Failed to fetch address suggestions');
      }
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchAddresses(value);
    }, 300);
  };

  // Handle selection of an address suggestion
  const handleSelectAddress = async (prediction) => {
    setInputValue(prediction.description);
    setShowSuggestions(false);
    setSuggestions([]);

    try {
      // Fetch detailed place information
      const response = await axios.get(`${getApiBase()}/places/details`, {
        params: {
          place_id: prediction.place_id
        },
        headers: getAuthHeaders()
      });

      if (response.data.status === 'OK' && response.data.result) {
        const place = response.data.result;
        const addressComponents = place.address_components || [];

        // Parse address components
        const components = {};
        addressComponents.forEach(comp => {
          const types = comp.types || [];
          if (types.includes('street_number')) components.streetNumber = comp.long_name;
          if (types.includes('route')) components.streetName = comp.long_name;
          if (types.includes('locality')) components.city = comp.long_name;
          if (!components.city && types.includes('sublocality_level_1')) components.city = comp.long_name;
          if (!components.city && types.includes('sublocality')) components.city = comp.long_name;
          if (!components.city && types.includes('neighborhood')) components.city = comp.long_name;
          if (types.includes('administrative_area_level_1')) components.state = comp.short_name;
          if (types.includes('postal_code')) components.postalCode = comp.long_name;
          if (types.includes('postal_code_suffix') && components.postalCode) {
            components.postalCode = `${components.postalCode}-${comp.long_name}`;
          }
          if (types.includes('country')) {
            components.countryCode = comp.short_name;
            components.countryName = comp.long_name;
          }
        });

        const addressLine1 = [components.streetNumber, components.streetName].filter(Boolean).join(' ');

        const updatedAddress = {
          addressLine1: addressLine1 || '',
          addressLine2: '',
          city: components.city || '',
          state_province: components.state || '',
          postalCode: components.postalCode || '',
          countryCode: components.countryCode || '',
          countryName: components.countryName || '',
          place_id: place.place_id || ''
        };

        setLocalAddress(updatedAddress);
        onChange(updatedAddress);
      }
    } catch (err) {
      console.error('Error fetching place details:', err);
      // Fall back to parsing from the prediction description
      const parsed = parseAddressFromDescription(prediction.description);
      if (parsed) {
        setLocalAddress(parsed);
        onChange(parsed);
      }
    }
  };

  // Fallback: parse address from description string
  const parseAddressFromDescription = (description) => {
    if (!description) return null;

    // Try to parse US address format: "123 Main St, City, ST 12345, USA"
    const usPattern = /^(.+?),\s*(.+?),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?(?:,\s*(?:USA|United States))?$/i;
    const match = description.match(usPattern);

    if (match) {
      const [, streetAddress, city, state, postalCode] = match;
      return {
        addressLine1: streetAddress?.trim() || '',
        addressLine2: '',
        city: city?.trim() || '',
        state_province: state?.trim() || '',
        postalCode: postalCode?.trim() || '',
        countryCode: 'US',
        countryName: 'United States'
      };
    }

    // For other formats, just put the whole thing in addressLine1
    return {
      addressLine1: description,
      addressLine2: '',
      city: '',
      state_province: '',
      postalCode: '',
      countryCode: '',
      countryName: ''
    };
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    if (inputValue && inputValue.length >= 3) {
      searchAddresses(inputValue);
    }
  };

  const handleManualAddressChange = (field, value) => {
    const updatedAddress = {
      ...localAddress,
      [field]: value
    };
    setLocalAddress(updatedAddress);
    onChange(updatedAddress);
  };

  return (
    <div className="space-y-4">
      {/* Address Search */}
      <div className="relative">
        <label htmlFor={`${instanceId}-address-search`} className="text-xs font-semibold text-slate-600 block mb-2">
          Search Address
        </label>
        <input
          ref={inputRef}
          id={`${instanceId}-address-search`}
          name={`${instanceId}-address-search`}
          type="text"
          autoComplete="off"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Type to search for an address..."
          className="w-full h-12 px-4 border-2 border-violet-200 rounded-xl focus:border-violet-400 focus:ring-violet-400 focus:ring-2 focus:ring-opacity-20 transition-all bg-white"
        />

        {/* Error message */}
        {error && !loading && (
          <div className="absolute z-10 w-full bg-yellow-50 border border-yellow-300 rounded-md shadow-lg mt-1 p-2">
            <div className="text-xs text-yellow-700">
              {error}
            </div>
          </div>
        )}

        {/* Suggestions dropdown */}
        {showSuggestions && (suggestions.length > 0 || loading) && !error && (
          <div className="absolute z-50 w-full bg-white border-2 border-violet-300 rounded-lg shadow-lg max-h-64 overflow-y-auto mt-1">
            {loading && (
              <div className="px-4 py-3 text-sm text-gray-500">
                Searching addresses...
              </div>
            )}

            {!loading && suggestions.map((prediction) => (
              <button
                key={prediction.place_id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectAddress(prediction)}
                className="w-full text-left px-4 py-3 hover:bg-violet-50 focus:bg-violet-50 focus:outline-none border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900">
                  {prediction.structured_formatting?.main_text || prediction.description}
                </div>
                {prediction.structured_formatting?.secondary_text && (
                  <div className="text-sm text-gray-500">
                    {prediction.structured_formatting.secondary_text}
                  </div>
                )}
              </button>
            ))}

            {!loading && suggestions.length === 0 && inputValue && inputValue.length >= 3 && (
              <div className="px-4 py-3 text-sm text-gray-500">
                No addresses found
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-slate-500 mt-1">
          Start typing to search for an address
        </p>
      </div>

      {/* Individual Address Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div className="sm:col-span-2">
          <label htmlFor={`${instanceId}-addressLine1`} className="text-xs font-semibold text-slate-600 flex items-center gap-1">
            Address Line 1 *
            {localAddress?.addressLine1 && <span className="text-green-600">✓</span>}
          </label>
          <input
            id={`${instanceId}-addressLine1`}
            name={`${instanceId}-addressLine1`}
            autoComplete="street-address"
            value={localAddress?.addressLine1 || ''}
            onChange={(e) => handleManualAddressChange('addressLine1', e.target.value)}
            placeholder="123 Main St"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
        </div>
        <div>
          <label htmlFor={`${instanceId}-city`} className="text-xs font-semibold text-slate-600 flex items-center gap-1">
            City *
            {localAddress?.city && <span className="text-green-600">✓</span>}
          </label>
          <input
            id={`${instanceId}-city`}
            name={`${instanceId}-city`}
            autoComplete="address-level2"
            value={localAddress?.city || ''}
            onChange={(e) => handleManualAddressChange('city', e.target.value)}
            placeholder="New York"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
        </div>
        <div>
          <label htmlFor={`${instanceId}-state_province`} className="text-xs font-semibold text-slate-600 flex items-center gap-1">
            State / Province
            {localAddress?.state_province && <span className="text-green-600">✓</span>}
          </label>
          <input
            id={`${instanceId}-state_province`}
            name={`${instanceId}-state_province`}
            autoComplete="address-level1"
            value={localAddress?.state_province || ''}
            onChange={(e) => handleManualAddressChange('state_province', e.target.value)}
            placeholder="NY"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
        </div>
        <div>
          <label htmlFor={`${instanceId}-postalCode`} className="text-xs font-semibold text-slate-600 flex items-center gap-1">
            Postal Code
            {localAddress?.postalCode && <span className="text-green-600">✓</span>}
          </label>
          <input
            id={`${instanceId}-postalCode`}
            name={`${instanceId}-postalCode`}
            autoComplete="postal-code"
            value={localAddress?.postalCode || ''}
            onChange={(e) => handleManualAddressChange('postalCode', e.target.value)}
            placeholder="10001"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
        </div>
        <div>
          <label htmlFor={`${instanceId}-countryName`} className="text-xs font-semibold text-slate-600 flex items-center gap-1">
            Country *
            {localAddress?.countryName && <span className="text-green-600">✓</span>}
          </label>
          <input
            id={`${instanceId}-countryName`}
            name={`${instanceId}-countryName`}
            autoComplete="country-name"
            value={localAddress?.countryName || ''}
            onChange={(e) => handleManualAddressChange('countryName', e.target.value)}
            placeholder="United States"
            className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
        </div>
      </div>
    </div>
  );
};

export default AddressAutofill;
